import { useState } from 'react';
import { Link2, Unlink, Plus, Trash2, ExternalLink, Loader2, Wifi, WifiOff } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useFigmaConnection,
  useFigmaFiles,
  useDisconnectFigma,
  useRemoveFileMapping,
  figmaKeys,
} from '../../hooks/useFigma';
import { getAuthUrl } from '../../services/figmaService';
import { useQueryClient } from '@tanstack/react-query';
import FigmaFileBrowserModal from './FigmaFileBrowserModal';
import type { ProjectDetail } from '../../services/projectService';

interface Props {
  project: ProjectDetail;
  isProjectManager: boolean;
}

export default function FigmaSettingsTab({ project, isProjectManager }: Props) {
  const queryClient = useQueryClient();
  const { data: connectionStatus, isLoading: isConnectionLoading } = useFigmaConnection(project.id);
  const { data: filesData, isLoading: isFilesLoading } = useFigmaFiles(
    project.id,
    connectionStatus?.connected ?? false,
  );
  const disconnectMutation = useDisconnectFigma();
  const removeFileMutation = useRemoveFileMapping();

  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const isConnected = connectionStatus?.connected ?? false;

  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      // API 호출로 OAuth redirect URL을 받아서 이동 (Bearer 토큰 포함)
      const authUrl = await getAuthUrl(project.id);
      window.location.href = authUrl;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Figma 연결을 시작할 수 없습니다.';
      toast.error(msg);
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync(project.id);
      toast.success('Figma 연결이 해제되었습니다.');
      setShowDisconnectConfirm(false);
    } catch {
      toast.error('Figma 연결 해제에 실패했습니다.');
    }
  };

  const handleRemoveFile = async (figmaFileId: string, fileName: string) => {
    if (!confirm(`'${fileName}' 파일 매핑을 제거하시겠습니까?`)) return;
    try {
      await removeFileMutation.mutateAsync({ projectId: project.id, figmaFileId });
      toast.success(`'${fileName}' 매핑이 제거되었습니다.`);
    } catch {
      toast.error('파일 매핑 제거에 실패했습니다.');
    }
  };

  if (isConnectionLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 연결 상태 섹션 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Figma 연결</h3>
            {!isConnected ? (
              <p className="text-sm text-gray-500 mt-1">
                Figma를 연결하면 디자인 파일을 프로젝트에 매핑하고, 디자인 항목을 자동으로 관리할 수 있습니다.
              </p>
            ) : (
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                {connectionStatus?.figmaTeamId && (
                  <span>팀: {connectionStatus.figmaTeamId}</span>
                )}
                {connectionStatus?.connectedBy && (
                  <span>연결자: {connectionStatus.connectedBy.name}</span>
                )}
                {connectionStatus?.connectedAt && (
                  <span>
                    연결일: {new Date(connectionStatus.connectedAt).toLocaleDateString('ko-KR')}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  {connectionStatus?.webhookActive ? (
                    <>
                      <Wifi size={12} className="text-green-500" />
                      <span className="text-green-600">Webhook 활성</span>
                    </>
                  ) : (
                    <>
                      <WifiOff size={12} className="text-gray-400" />
                      <span>Webhook 비활성</span>
                    </>
                  )}
                </span>
              </div>
            )}
          </div>

          {isProjectManager && (
            <>
              {!isConnected ? (
                <button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium
                    hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {isConnecting ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
                  {isConnecting ? '연결 중...' : 'Figma 연결'}
                </button>
              ) : (
                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-sm
                    hover:bg-red-50 transition-colors"
                >
                  <Unlink size={14} />
                  연결 해제
                </button>
              )}
            </>
          )}
        </div>

        {/* 연결 해제 확인 */}
        {showDisconnectConfirm && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 mb-3">
              Figma 연결을 해제하시겠습니까?
              <br />
              <span className="text-sm">이미 생성된 디자인 항목과 리뷰 이력은 삭제되지 않습니다.</span>
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm
                  hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {disconnectMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                연결 해제 확인
              </button>
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 파일 매핑 섹션 (연결 후에만 표시) */}
      {isConnected && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">매핑된 Figma 파일</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {filesData?.files.length ?? 0}개 파일 매핑됨
              </p>
            </div>
            {isProjectManager && (
              <button
                onClick={() => setShowFileBrowser(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium
                  hover:bg-primary-700 transition-colors"
              >
                <Plus size={14} />
                파일 추가
              </button>
            )}
          </div>

          {isFilesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-gray-400" />
            </div>
          ) : filesData?.files.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              매핑된 파일이 없습니다. [파일 추가] 버튼으로 Figma 파일을 연결하세요.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filesData?.files.map((file) => (
                <div key={file.figmaFileId} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.fileName}</p>
                    <p className="text-sm text-gray-400">
                      {file.lastModified
                        ? `마지막 수정: ${new Date(file.lastModified).toLocaleString('ko-KR')}`
                        : '수정일 정보 없음'}
                    </p>
                  </div>

                  <a
                    href={`https://www.figma.com/file/${file.fileKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded transition-colors"
                    title="Figma에서 열기"
                  >
                    <ExternalLink size={16} />
                  </a>

                  {isProjectManager && (
                    <button
                      onClick={() => handleRemoveFile(file.figmaFileId, file.fileName)}
                      disabled={removeFileMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                      title="매핑 제거"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 파일 탐색 모달 */}
      {showFileBrowser && (
        <FigmaFileBrowserModal
          projectId={project.id}
          mappedFileKeys={new Set(filesData?.files.map((f) => f.fileKey) ?? [])}
          onClose={() => setShowFileBrowser(false)}
          onAdded={() => {
            queryClient.invalidateQueries({ queryKey: figmaKeys.files(project.id) });
          }}
        />
      )}
    </div>
  );
}
